import type { Scene, SceneElement } from './types.js';

/**
 * Where the shopper dragged a movable layer — Customily's "movable text".
 *
 * The answer lives beside the option answers under the key `__placement`,
 * keyed by ELEMENT id: `{ [elementId]: { dx, dy } }`. The offsets are in units
 * of the layer's own width and height rather than pixels, because the same
 * layer is a different size on the print file and on the mockup, and one
 * gesture on the preview has to land in the same place on both.
 *
 * Only layers the author opened up (`shopper.move`) obey it, and never by more
 * than a few widths: a value is browser input, and a runaway offset would put
 * the text off the product.
 */

export const PLACEMENT_KEY = '__placement';

export interface ShopperPlacement {
  dx: number;
  dy: number;
  /**
   * The layer's size relative to its authored size (1 = as designed). Only a
   * layer the author let the shopper resize (`shopper.scale`) obeys it; text
   * keeps its box proportions and re-fits inside the bigger or smaller box.
   */
  scale?: number;
}

const LIMIT = 3;
const SCALE_MIN = 0.25;
const SCALE_MAX = 4;

/** The placements out of a values map, or nothing when there are none. */
export function readPlacements(values: Record<string, unknown>): Record<string, ShopperPlacement> {
  const raw = values[PLACEMENT_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, ShopperPlacement> = {};
  for (const [id, entry] of Object.entries(raw as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object') continue;
    const { dx, dy, scale } = entry as { dx?: unknown; dy?: unknown; scale?: unknown };
    const x = Number(dx);
    const y = Number(dy);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const s = Number(scale);
    out[id] = {
      dx: Math.max(-LIMIT, Math.min(LIMIT, x)),
      dy: Math.max(-LIMIT, Math.min(LIMIT, y)),
      ...(Number.isFinite(s) && s > 0 && Math.abs(s - 1) > 0.001
        ? { scale: Math.max(SCALE_MIN, Math.min(SCALE_MAX, s)) }
        : {}),
    };
  }
  return out;
}

/** Returns the scene with every movable layer shifted (and resized) by its placement. */
export function applyShopperPlacements(
  scene: Scene,
  placements: Record<string, ShopperPlacement>,
): Scene {
  const ids = Object.keys(placements);
  if (ids.length === 0) return scene;
  let changed = false;
  const elements = scene.elements.map((el): SceneElement => {
    const place = placements[el.id];
    if (!place || !el.transform) return el;
    const moves = Boolean(el.shopper?.move) && (place.dx !== 0 || place.dy !== 0);
    const scale = el.shopper?.scale && place.scale ? place.scale : 1;
    if (!moves && scale === 1) return el;
    changed = true;
    const t = el.transform;
    // Offsets are in units of the AUTHORED size, so a drag means the same
    // distance whether or not the shopper also resized the layer.
    const next: SceneElement = {
      ...el,
      transform: {
        ...t,
        cx: t.cx + (el.shopper?.move ? place.dx * t.w : 0),
        cy: t.cy + (el.shopper?.move ? place.dy * t.h : 0),
        w: t.w * scale,
        h: t.h * scale,
      },
    } as SceneElement;
    // A resized text box keeps its type scale: the fit limits grow with the box.
    if (scale !== 1 && next.type === 'text') {
      const font = next.font;
      next.font = {
        ...font,
        size: font.size === undefined ? undefined : font.size * scale,
        minSize: font.minSize === undefined ? undefined : font.minSize * scale,
        maxSize: font.maxSize === undefined ? undefined : font.maxSize * scale,
      };
    }
    return next;
  });
  return changed ? { ...scene, elements } : scene;
}
